import { obsTimeoutMs } from './run-with-timeout';
import type { ObsRunner } from './page-live-effect-livestream.component';

/** Tên khoá dùng chung cho mọi nguồn overlay có #obs= trên cùng máy (cùng trình duyệt OBS). */
export const OBS_LOCK_NAME = 'cab-live-effect-obs';
/** Lượt trò mạnh đã chạy gần đây, để nguồn overlay thứ hai không chạy lại cùng lượt sau khi chờ khoá. */
const DONE_KEY = 'cab-live-effect-obs-done';
const MAX_DONE = 50;

type LockLike = {
  request<T>(
    name: string,
    options: { signal?: AbortSignal; ifAvailable?: boolean },
    callback: (lock: unknown) => Promise<T>
  ): Promise<T>;
};
type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

const defaultLocks = (): LockLike | undefined =>
  typeof navigator !== 'undefined' && navigator.locks
    ? (navigator.locks as unknown as LockLike)
    : undefined;

const defaultStorage = (): StorageLike | undefined => {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const readDone = (storage?: StorageLike): string[] => {
  try {
    const list = JSON.parse(storage?.getItem(DONE_KEY) ?? '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const isDone = (storage: StorageLike | undefined, triggerId?: string | null) =>
  !!triggerId && readDone(storage).includes(triggerId);

/** Ghi lượt vào danh sách đã chạy, chỉ sau khi trò chạy xong (trang tắt giữa chừng hay trò lỗi thì nguồn kia vẫn chạy). */
const markDone = (
  storage: StorageLike | undefined,
  triggerId?: string | null
) => {
  if (!triggerId || !storage) return;
  try {
    storage.setItem(
      DONE_KEY,
      JSON.stringify([...readDone(storage), triggerId].slice(-MAX_DONE))
    );
  } catch {
    /* hết chỗ lưu: chỉ mất phần bỏ trùng giữa hai nguồn */
  }
};

/**
 * Hai nguồn overlay có #obs= trên cùng máy dùng chung một bản lưu hình gốc trong localStorage: nếu cùng chạy trò mạnh
 * thì bản lưu bị ghi đè, hình không trả về đúng. Bọc runner để mỗi lần chạy trò mạnh phải giữ khoá chung
 * (navigator.locks); lượt mà nguồn kia đã chạy xong thì bỏ qua. Chờ khoá quá hạn chót của lượt thì thôi không chạy nữa.
 * reset khi nguồn này không giữ khoá chỉ huỷ lượt đang chờ, và chỉ trả hình khi không nguồn nào đang chạy trò
 * (không bao giờ đụng bản lưu của nguồn kia đang chạy). Trình duyệt không có navigator.locks thì chạy như cũ, không khoá.
 */
export const withObsLock = (
  runner: ObsRunner,
  locks: LockLike | undefined = defaultLocks(),
  storage: StorageLike | undefined = defaultStorage()
): ObsRunner => {
  if (!locks) return runner;
  /** Nguồn này đang giữ khoá và chạy trò. */
  let held = false;
  /** Các lượt đang chờ khoá (huỷ được khi reset hoặc quá hạn). */
  const waits = new Set<AbortController>();
  return {
    run: (ev, overlay, hooks) => {
      const ctrl = new AbortController();
      waits.add(ctrl);
      const timer = setTimeout(
        () => ctrl.abort(),
        obsTimeoutMs(ev.durationSec)
      );
      const stopWaiting = () => {
        clearTimeout(timer);
        waits.delete(ctrl);
      };
      return locks
        .request<'ok' | 'source_missing'>(
          OBS_LOCK_NAME,
          { signal: ctrl.signal },
          async () => {
            stopWaiting();
            // Bị huỷ đúng lúc vừa được khoá: không chạy trò trễ.
            if (ctrl.signal.aborted) throw new Error('Đã huỷ chờ khoá OBS');
            if (isDone(storage, ev.triggerId)) return 'ok';
            held = true;
            try {
              hooks?.onStart?.();
              const res = await runner.run(ev, overlay);
              markDone(storage, ev.triggerId);
              return res;
            } finally {
              held = false;
            }
          }
        )
        .finally(stopWaiting);
    },
    reset: async () => {
      waits.forEach((c) => c.abort());
      waits.clear();
      if (held) {
        await runner.reset();
        return;
      }
      await locks.request(
        OBS_LOCK_NAME,
        { ifAvailable: true },
        async (lock) => {
          if (lock) await runner.reset();
        }
      );
    },
    connected: () => runner.connected(),
  };
};

/** Chạy một việc OBS (vd trả hình còn sót lúc mới nối) trong cùng khoá, để không trả nhầm bản lưu của nguồn kia đang chạy. */
export const runWithObsLock = <T>(
  task: () => Promise<T>,
  locks: LockLike | undefined = defaultLocks()
): Promise<T> =>
  locks ? locks.request(OBS_LOCK_NAME, {}, () => task()) : task();
